import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";

import { queryKeys, useProjectOverviewQuery } from "@/api/hooks";
import {
  usePreviewCandidatesQuery,
  usePreviewPatientsQuery,
  type CandidateSummary,
} from "@/api/preview";
import { classifyRunReadFailure } from "@/api/previewInsights";
import {
  classifyReplayFailure,
  usePreviewRunTurnsQuery,
  useReplayPreviewRunMutation,
  type ReplayFailure,
  type TurnEventsReading,
} from "@/api/previewReplay";
import { useCapability } from "@/auth/SessionContext";
import { isCurrent } from "@/lib/candidateFreshness";
import { formatDateTime, shortHash } from "@/lib/format";
import {
  CAPTURE_STATE_LABELS,
  RUN_POLL_INTERVAL_MS,
  SYNTHETIC_PREVIEW_DISCLAIMER,
  previewRunPath,
  type PreviewRunDispatch,
} from "@/lib/previewInsights";
import {
  PREVIEW_REPLAY_MAX_TURNS,
  REPLAY_RESULT_LABELS,
  REPLAY_RULES,
  describeReplayProgress,
  describeReplayRefusal,
  describeReplayScope,
  plannedTurns,
  readReplayProgress,
  readReplayResult,
  readTurnBoundaries,
  replayRequestBody,
  type ReplayBoundary,
  type ReplayRefusalRecovery,
  type ReplayResultView,
} from "@/lib/previewReplay";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { LoadingState } from "@/ui/states";
import {
  admissionRefusalSentence,
  refusalRecovery,
} from "@/ui/preview/finalQuestioningRefusal";
import { PREVIEW_TARGET_NOT_ENABLED_COPY } from "@/ui/preview/PreviewTargetNotice";

/**
 * Replay from a turn — the command dialog (#1105, approved design §11.1).
 *
 * Opened from a boundary on the run page's `Replay from a turn` panel, never
 * from an address: the route table deliberately holds no `/replays` route
 * (`src/invariants/routes.test.ts`), because a replay is a command a
 * clinician issues from a run they are looking at, not a place.
 *
 * WHAT THE CLINICIAN CHOOSES, and all they choose: the immutable testing
 * version the replay runs against, the synthetic fixture the new conversation
 * runs as, and a confirmation of the preview target and revision the command
 * will be decided against. The turn boundary arrived with the opener. Those
 * four selections become exactly the contract's fields (`replayRequestBody`);
 * nothing a tester typed, no hash, no event, no Liv or Brain identity has a
 * field to travel in, and the source run is named only by the locator in the
 * path. `src/invariants/previewEgress.test.tsx` holds that on the bytes.
 *
 * ONE ATTEMPT, ONE KEY. The idempotency key is minted when the clinician
 * first presses Start and kept for as long as the same selections stand: a
 * retry after a lost answer re-presents it, and the service answers from its
 * reservation without sending a second command (#1104). Any change of
 * selection, a moved target revision, or a refusal that names the key retires
 * it — the key identifies the whole tuple, and a key re-used with a different
 * tuple is refused as a conflict rather than answered.
 *
 * PROGRESS IS READ FROM THE NEW RUN, never asked of the command again: every
 * turn the destination's own trace records is a source input that was
 * delivered and answered. The refresh control re-reads that; it is a GET and
 * is always safe to press.
 */

type Attempt =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "answered"; result: ReplayResultView }
  | { kind: "failed"; failure: ReplayFailure };

type CandidateCardState = "current" | "previous" | "expired";

const CANDIDATE_STATE_LABEL: Record<CandidateCardState, string> = {
  current: "Current",
  previous: "Previous",
  expired: "Expired",
};

/** The service's validity reading, never a browser clock. */
function isExpired(candidate: CandidateSummary): boolean {
  return candidate.validity?.state === "expired";
}

function candidateState(candidate: CandidateSummary): CandidateCardState {
  return isExpired(candidate) ? "expired" : isCurrent(candidate) ? "current" : "previous";
}

function versionLabel(candidate: CandidateSummary): string {
  return candidate.revision
    ? `Test version ${candidate.revision.number}`
    : "Test version";
}

/**
 * The command's refusals, one sentence each. Where the state is the one the
 * preview dialog already renders for session creation, the sentence is the
 * same sentence — one copy per refusal, wherever it is rendered.
 */
function failureSentence(failure: ReplayFailure): string {
  switch (failure.kind) {
    case "stale_target":
      return (
        "This project's preview target changed after you read it, so nothing was replayed: a replay dispatched now would run against a target nobody on this screen confirmed." +
        (failure.currentRevision === undefined
          ? " The Editor Service reports a different revision now."
          : ` The Editor Service now reports revision ${failure.currentRevision}.`) +
        " Reload the target, confirm the new revision, and start again."
      );
    case "duplicate_command":
      return "This attempt's command id already names a different replay command, so the Editor Service refused it without replaying anything and without disclosing the other command. Start a fresh attempt: it uses a new command id.";
    case "admission_refused":
      return `The replay was refused because this test version's safety posture could not be cleared for the preview target, so no preview session was started. ${admissionRefusalSentence(failure)}`;
    case "preview_target":
      return PREVIEW_TARGET_NOT_ENABLED_COPY;
    case "candidate_unusable":
      return "The Editor Service would not replay into that test version: it is not from this project, or it has expired. Nothing was replayed; re-read the available versions and choose again.";
    case "candidate_validator_mismatch":
      return "This immutable test version was validated by a different Brain build than this deployment uses. Nothing was replayed; open Preview and rebuild a test version on this deployment.";
    case "patient_gone":
      return "The preview target no longer offers that test patient — the fixture list this dialog is showing has gone stale. Nothing was replayed; look for test patients again and choose one of the current fixtures.";
    case "invalid_turn":
      return `The Editor Service refused the chosen turn boundary: it is outside the preview target's published turn budget of ${PREVIEW_REPLAY_MAX_TURNS}. Nothing was replayed; choose an earlier turn.`;
    case "source_unavailable":
      return "The source run is not available to replay from at this address for your account in this environment: the link may be out of date, the run may belong to a different project or deployment, or its preview target may no longer hold it. Nothing was replayed.";
    case "denied":
      return "You are not authorized to create a preview session for this project, so no replay was started. Ask a workspace administrator for the preview.create permission.";
    case "unauthenticated":
      return "Your sign-in has expired, so the replay was not started. Sign in again and start it again.";
    case "unavailable":
      return "This deployment of the Editor Service does not serve preview replays. That is a property of the deployment rather than of anything on this screen, so there is nothing here to retry.";
    case "unknown_dispatch":
      return failure.answered
        ? "The preview target could not be reached or did not answer, so whether the replay started is unknown; nothing on this screen assumes either way. Retrying the same attempt is safe because the Editor Service answers from this command's reservation and cannot spend a second budget."
        : "No response arrived from the Editor Service, so whether the replay started is unknown. Check the connection and retry the same attempt: it answers from the reservation of this command and cannot spend a second budget.";
    case "validator_pin_unavailable":
      return "This Editor deployment cannot establish its configured Brain build, so it refused the replay before reservation or egress. Ask a workspace administrator to repair the deployment pin.";
    case "error":
      return `The Editor Service answered with "${failure.code}"${
        failure.requestId ? ` (request ${failure.requestId})` : ""
      }. Nothing on this screen assumes a replay started; trying again re-presents the same attempt.`;
  }
}

const FAILURE_RECOVERY_LABEL = {
  stale_target: "Reload the preview target",
  duplicate_command: "Start a fresh attempt",
  admission_refused: "Choose another test version",
  preview_target: "Check the target again",
  candidate_unusable: "Look for test versions again",
  candidate_validator_mismatch: "Open Preview to rebuild",
  patient_gone: "Look for test patients again",
  invalid_turn: "Choose another turn",
  source_unavailable: null,
  denied: null,
  unauthenticated: null,
  unavailable: null,
  unknown_dispatch: "Retry the same attempt",
  validator_pin_unavailable: null,
  error: "Try again",
} satisfies Record<ReplayFailure["kind"], string | null>;

const REFUSAL_RECOVERY_LABEL = {
  refresh_turns: "Re-read the turns",
  choose_earlier_turn: "Choose an earlier turn",
  fresh_attempt: "Start a fresh attempt",
  wait: "Close and try later",
  operator: null,
  open_run: null,
  none: null,
} satisfies Record<ReplayRefusalRecovery, string | null>;

export function ReplayFromTurnDialog({
  projectId,
  sourcePreviewRunRef,
  boundary,
  visibleTurnCount,
  sourceDispatch,
  onClose,
  onRefreshTurns,
  pollIntervalMs = RUN_POLL_INTERVAL_MS,
}: {
  projectId: string;
  /** The SOURCE run's opaque locator — the only thing that names it. */
  sourcePreviewRunRef: string;
  boundary: ReplayBoundary;
  /** How many turns the source's trace shows, for the scope sentence. */
  visibleTurnCount: number;
  /** The source run's dispatch record: which version it ran, under which revision. */
  sourceDispatch: PreviewRunDispatch;
  onClose: () => void;
  /** Re-reads the source's turn list — a refusal's recovery when the script moved. */
  onRefreshTurns: () => void;
  /** How often the new run's progress is re-read. Tests shorten it. */
  pollIntervalMs?: number;
}) {
  const queryClient = useQueryClient();
  const inspect = useCapability("preview.inspect");
  const create = useCapability("preview.create");
  const overview = useProjectOverviewQuery(projectId, { alwaysRefetchOnMount: true });
  const candidates = usePreviewCandidatesQuery(projectId);
  const patients = usePreviewPatientsQuery(projectId);
  const replay = useReplayPreviewRunMutation(projectId, sourcePreviewRunRef);

  const [chosenCandidateId, setCandidateId] = useState<string | null>(null);
  const [chosenPatientRef, setPatientRef] = useState<string | null>(null);
  const [confirmedRevision, setConfirmedRevision] = useState<number | null>(null);
  const [attempt, setAttempt] = useState<Attempt>({ kind: "idle" });
  const [revisionMoved, setRevisionMoved] = useState<{ from: number; to: number } | null>(null);
  const attemptKey = useRef<string | null>(null);

  const ids = useId();
  const candidateGroup = `${ids}-candidate`;
  const patientGroup = `${ids}-patient`;
  const confirmId = `${ids}-confirm`;
  const confirmDescriptionId = `${ids}-confirm-description`;
  const startReasonId = `${ids}-start-reason`;

  const project = overview.data?.project ?? null;
  const revision = project?.previewTargetRevision ?? null;

  /**
   * A moved target revision retires the confirmation, the key and any
   * unanswered attempt: the revision this dialog sends is the one the
   * clinician confirmed, and one that moved underneath them was not.
   */
  const seenRevision = useRef<number | null>(null);
  useEffect(() => {
    if (revision === null) {
      return;
    }
    if (seenRevision.current !== null && seenRevision.current !== revision) {
      setRevisionMoved({ from: seenRevision.current, to: revision });
      setConfirmedRevision(null);
      attemptKey.current = null;
      setAttempt((current) => (current.kind === "answered" ? current : { kind: "idle" }));
      void queryClient.invalidateQueries({ queryKey: queryKeys.previewPatients(projectId) });
    }
    seenRevision.current = revision;
  }, [revision, projectId, queryClient]);

  /**
   * A choice the latest DEFINED listing no longer offers is retired during
   * render — the preview dialog's reconciliation, for the same reason: the
   * command must never name a version or fixture this dialog cannot display.
   */
  const candidateItems = candidates.data?.items;
  const candidateWithdrawn =
    chosenCandidateId !== null &&
    candidateItems !== undefined &&
    !candidateItems.some((item) => item.candidateId === chosenCandidateId);
  if (candidateWithdrawn) {
    setCandidateId(null);
  }
  const candidateId = candidateWithdrawn ? null : chosenCandidateId;

  const patientItems = patients.data?.items;
  const patientWithdrawn =
    chosenPatientRef !== null &&
    patientItems !== undefined &&
    !patientItems.some((item) => item.ref === chosenPatientRef);
  if (patientWithdrawn) {
    setPatientRef(null);
  }
  const patientRef = patientWithdrawn ? null : chosenPatientRef;

  /** Every selection change is a different command: the key goes with it. */
  function retireAttempt() {
    attemptKey.current = null;
    setAttempt({ kind: "idle" });
  }
  function chooseCandidate(id: string) {
    setCandidateId(id);
    retireAttempt();
  }
  function choosePatient(ref: string) {
    setPatientRef(ref);
    retireAttempt();
  }
  function confirm(checked: boolean) {
    setConfirmedRevision(checked ? revision : null);
    if (revisionMoved !== null && checked) {
      setRevisionMoved(null);
    }
    retireAttempt();
  }

  const chosenCandidate =
    candidateItems?.find((item) => item.candidateId === candidateId) ?? null;
  const sending = attempt.kind === "sending";
  const candidatesUnreadable = candidates.isError && candidates.data === undefined;
  const patientsUnreadable = patients.isError && patients.data === undefined;

  const startReason = !inspect.allowed
    ? `${inspect.reason ?? ""} Starting a replay needs inspect access to the source run and the "preview.inspect" permission.`
    : !create.allowed
      ? `${create.reason ?? ""} Starting a replay creates a new preview session, which needs the "preview.create" permission.`
      : project === null
        ? "The project could not be read, so the preview target to replay against is unknown."
        : candidatesUnreadable
          ? "The test versions could not be listed, so no version can be chosen."
          : candidateId === null
            ? "Choose the testing version to replay against."
            : chosenCandidate !== null && isExpired(chosenCandidate)
              ? "That testing version has expired and can no longer be loaded."
              : patientsUnreadable
                ? "The test patients could not be listed, so no fixture can be chosen."
                : patientRef === null
                  ? "Choose the test patient the new conversation runs as."
                  : confirmedRevision === null || confirmedRevision !== revision
                    ? "Confirm the preview target and revision the replay runs against."
                    : null;
  const ready = startReason === null && !sending && attempt.kind !== "answered";

  async function start() {
    if (!ready || candidateId === null || patientRef === null || revision === null) {
      return;
    }
    attemptKey.current ??= crypto.randomUUID();
    setAttempt({ kind: "sending" });
    try {
      const created = await replay.mutateAsync(
        replayRequestBody({
          candidateId,
          patientRef,
          expectedPreviewTargetRevision: revision,
          idempotencyKey: attemptKey.current,
          boundary,
        }),
      );
      // Answered: this key has done its work and must never name another
      // command. A later "start another replay" mints a new one.
      attemptKey.current = null;
      setAttempt({ kind: "answered", result: readReplayResult(created) });
    } catch (error) {
      const failure = classifyReplayFailure(error);
      if (failure.kind === "stale_target" || failure.kind === "duplicate_command") {
        // The tuple this key named is gone (revision) or already taken:
        // the recovery mints a new key. Every other failure keeps it, so a
        // retry re-presents the same command and cannot spend twice.
        attemptKey.current = null;
      }
      setAttempt({ kind: "failed", failure });
    }
  }

  function recover(failure: ReplayFailure) {
    switch (failure.kind) {
      case "stale_target":
        void queryClient.invalidateQueries({ queryKey: queryKeys.projectOverview(projectId) });
        setConfirmedRevision(null);
        setAttempt({ kind: "idle" });
        return;
      case "duplicate_command":
      case "error":
      case "unknown_dispatch":
        // `duplicate_command` already dropped its key; the other two keep it,
        // which is the point of pressing the control.
        if (failure.kind === "duplicate_command") {
          setAttempt({ kind: "idle" });
          return;
        }
        void start();
        return;
      case "admission_refused":
        void queryClient.invalidateQueries({
          queryKey: queryKeys.candidateRuntimeDetections(projectId),
        });
        setCandidateId(null);
        retireAttempt();
        return;
      case "preview_target":
        void queryClient.invalidateQueries({ queryKey: queryKeys.previewTargets });
        setAttempt({ kind: "idle" });
        return;
      case "candidate_unusable":
        void queryClient.invalidateQueries({ queryKey: queryKeys.previewCandidates(projectId) });
        setCandidateId(null);
        retireAttempt();
        return;
      case "candidate_validator_mismatch":
      case "validator_pin_unavailable":
        return;
      case "patient_gone":
        void queryClient.invalidateQueries({ queryKey: queryKeys.previewPatients(projectId) });
        setPatientRef(null);
        retireAttempt();
        return;
      case "invalid_turn":
        onRefreshTurns();
        onClose();
        return;
      case "source_unavailable":
      case "denied":
      case "unauthenticated":
      case "unavailable":
        return;
    }
  }

  function recoverFromRefusal(recovery: ReplayRefusalRecovery) {
    switch (recovery) {
      case "refresh_turns":
      case "choose_earlier_turn":
        onRefreshTurns();
        onClose();
        return;
      case "fresh_attempt":
        retireAttempt();
        return;
      case "wait":
        onClose();
        return;
      case "operator":
      case "open_run":
      case "none":
        return;
    }
  }

  const sourceVersion = sourceDispatch.candidateRevision?.number ?? null;
  const targetId = project?.previewTenantId ?? null;
  const dispatchedAt = sourceDispatch.previewTargetRevision;
  const title =
    boundary.kind === "through_turn"
      ? `Replay through turn ${boundary.turnNumber}`
      : "Replay the whole script";

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !sending) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={title}
        description="Start a fresh synthetic preview that replays this run's user inputs into a testing version you choose. Nothing is sent to a real patient."
        busy={sending}
        className="w-[min(40rem,calc(100vw-2rem))]"
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={sending}>
              Close
            </Button>
            {attempt.kind === "answered" ? (
              <Button onClick={retireAttempt} variant="outline">
                Start another replay
              </Button>
            ) : attempt.kind === "failed" &&
              (attempt.failure.kind === "unknown_dispatch" ||
                attempt.failure.kind === "error") ? (
              <Button onClick={() => recover(attempt.failure)}>
                {FAILURE_RECOVERY_LABEL[attempt.failure.kind]}
              </Button>
            ) : (
              <Button
                onClick={() => void start()}
                disabled={!ready}
                aria-describedby={ready ? undefined : startReasonId}
              >
                {sending ? "Starting…" : "Start replay"}
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-5">
          <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs font-medium text-foreground">
            {SYNTHETIC_PREVIEW_DISCLAIMER}
          </p>

          {/* THE SCOPE: what is replayed and, just as plainly, what is not. */}
          <section aria-labelledby={`${ids}-scope`} className="space-y-2">
            <h3 id={`${ids}-scope`} className="text-sm font-semibold">
              What this replay does
            </h3>
            <p className="text-sm text-foreground">
              {describeReplayScope(boundary, visibleTurnCount)}
            </p>
            <ul className="list-disc space-y-1 ps-5 text-xs text-muted-foreground">
              <li>{REPLAY_RULES.inputsOnly}</li>
              <li>{REPLAY_RULES.nothingRestored}</li>
              <li>{REPLAY_RULES.freshPreview}</li>
            </ul>
          </section>

          {attempt.kind === "answered" ? (
            <ReplayOutcome
              projectId={projectId}
              result={attempt.result}
              planned={plannedTurns(boundary)}
              pollIntervalMs={pollIntervalMs}
              onRecover={recoverFromRefusal}
            />
          ) : (
            <>
              {revisionMoved === null ? null : (
                <p
                  role="status"
                  aria-live="polite"
                  className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground"
                >
                  {`This project's preview target moved from revision ${revisionMoved.from} to revision ${revisionMoved.to} while this dialog was open. Confirm the new revision before starting; the fixture list is being re-read.`}
                </p>
              )}

              {/* THE CANDIDATE: immutable, this project's, chosen explicitly. */}
              <fieldset className="space-y-2" disabled={sending}>
                <legend className="text-sm font-semibold">Test version to replay against</legend>
                <p className="text-xs text-muted-foreground">
                  {sourceVersion === null
                    ? "The version the source run was dispatched with is no longer listed for this project. Choose any version below; a different version from the source's is the point of a comparison, and a confounder for one."
                    : `The source run was dispatched with Test version ${sourceVersion}. Choose the same version to reproduce it, or another to test a change against the same inputs.`}
                </p>
                {candidates.isPending ? (
                  <LoadingState label="Loading test versions" rows={2} />
                ) : candidatesUnreadable ? (
                  <div role="alert" className="space-y-2 text-sm text-foreground">
                    <p>
                      The test versions for this project could not be listed, so
                      no version can be chosen. Nothing has been assumed about
                      which versions exist.
                    </p>
                    <Button variant="outline" onClick={() => void candidates.refetch()}>
                      Try again
                    </Button>
                  </div>
                ) : (candidateItems ?? []).length === 0 ? (
                  <p className="rounded-md border border-border px-3 py-3 text-xs text-muted-foreground">
                    This project has no frozen testing version to replay against.
                    Freeze one from the preview dialog first.
                  </p>
                ) : (
                  <div className="max-h-56 divide-y divide-border overflow-y-auto rounded-md border border-border">
                    {(candidateItems ?? []).map((candidate) => {
                      const state = candidateState(candidate);
                      const expired = state === "expired";
                      const noteId = `${ids}-candidate-${candidate.candidateId}-note`;
                      const isSource =
                        sourceVersion !== null &&
                        candidate.revision?.number === sourceVersion;
                      return (
                        <label
                          key={candidate.candidateId}
                          className={`flex items-start gap-3 px-3 py-2.5 ${
                            expired ? "text-muted-foreground" : "hover:bg-muted"
                          } ${candidateId === candidate.candidateId ? "bg-muted" : ""}`}
                        >
                          <input
                            type="radio"
                            name={candidateGroup}
                            value={candidate.candidateId}
                            checked={candidateId === candidate.candidateId}
                            disabled={expired}
                            aria-describedby={expired || isSource ? noteId : undefined}
                            onChange={() => chooseCandidate(candidate.candidateId)}
                            className="mt-1"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium">
                              {`${versionLabel(candidate)} · ${CANDIDATE_STATE_LABEL[state]}`}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              Frozen{" "}
                              <time dateTime={candidate.createdAt} dir="ltr">
                                {formatDateTime(candidate.createdAt)}
                              </time>
                              {" · "}
                              <code dir="ltr" className="font-mono" title={candidate.contentHash}>
                                {shortHash(candidate.contentHash)}
                              </code>
                            </span>
                            {expired ? (
                              <span id={noteId} className="block text-xs text-warning-foreground">
                                Expired: it can no longer be loaded, so it cannot be replayed against.
                              </span>
                            ) : isSource ? (
                              <span id={noteId} className="block text-xs text-muted-foreground">
                                The version the source run was dispatched with.
                              </span>
                            ) : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </fieldset>

              {/* THE FIXTURE: chosen here, not inherited — the dispatch records none. */}
              <fieldset className="space-y-2" disabled={sending}>
                <legend className="text-sm font-semibold">Test patient</legend>
                <p className="text-xs text-muted-foreground">
                  The new conversation runs as a designated test patient; real
                  patients are never shown. The source run's fixture is not
                  recorded here, so choose the same one for a like-for-like
                  replay. A different fixture is a confounder for any comparison.
                </p>
                {patients.isPending ? (
                  <LoadingState label="Loading test patients" rows={2} />
                ) : patientsUnreadable ? (
                  <div role="alert" className="space-y-2 text-sm text-foreground">
                    <p>
                      The test patients for this project&apos;s preview target
                      could not be listed. Nothing has been assumed about which
                      fixtures exist.
                    </p>
                    <Button variant="outline" onClick={() => void patients.refetch()}>
                      Try again
                    </Button>
                  </div>
                ) : (patientItems ?? []).length === 0 ? (
                  <p className="rounded-md border border-border px-3 py-3 text-xs text-muted-foreground">
                    No test patients are available for this preview target.
                  </p>
                ) : (
                  <div className="max-h-56 divide-y divide-border overflow-y-auto rounded-md border border-border">
                    {(patientItems ?? []).map((patient) => (
                      <label
                        key={patient.ref}
                        className={`flex items-center gap-3 px-3 py-2.5 hover:bg-muted ${
                          patientRef === patient.ref ? "bg-muted" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name={patientGroup}
                          value={patient.ref}
                          checked={patientRef === patient.ref}
                          onChange={() => choosePatient(patient.ref)}
                        />
                        <span dir="auto" className="text-sm font-medium">
                          {patient.label}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </fieldset>

              {/* THE TARGET AND ITS REVISION, confirmed rather than assumed. */}
              <section aria-labelledby={`${ids}-target`} className="space-y-2">
                <h3 id={`${ids}-target`} className="text-sm font-semibold">
                  Preview target
                </h3>
                {overview.isPending ? (
                  <LoadingState label="Loading the preview target" rows={1} />
                ) : project === null ? (
                  <p role="alert" className="text-sm text-foreground">
                    The project could not be read, so the preview target to
                    replay against is unknown. Close this and open the run again.
                  </p>
                ) : (
                  <>
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id={confirmId}
                        checked={confirmedRevision !== null && confirmedRevision === revision}
                        disabled={sending}
                        aria-describedby={confirmDescriptionId}
                        onChange={(event) => confirm(event.target.checked)}
                        className="mt-1"
                      />
                      <label htmlFor={confirmId} className="text-sm text-foreground">
                        Replay against the current preview target{" "}
                        <code dir="ltr" className="font-mono text-xs">
                          {targetId}
                        </code>{" "}
                        at revision <span dir="ltr">{revision}</span>
                      </label>
                    </div>
                    <p id={confirmDescriptionId} className="text-xs text-muted-foreground">
                      {dispatchedAt === revision
                        ? `The source run was dispatched under this same revision (${dispatchedAt}). The command names the revision you confirm; if the project is retargeted before it is sent, it is refused rather than run elsewhere.`
                        : `The source run was dispatched under revision ${dispatchedAt}; the project now points at revision ${revision}, so the replay runs against a different target state from the source. The command names the revision you confirm and is refused if it moves again.`}
                    </p>
                  </>
                )}
              </section>

              {attempt.kind === "failed" ? (
                <div role="alert" className="space-y-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-foreground">
                  <p>{failureSentence(attempt.failure)}</p>
                  {attempt.failure.kind === "admission_refused" &&
                  attempt.failure.admissionReason === "final_questioning_incompatible" &&
                  refusalRecovery(attempt.failure.refusalReason) !== null ? (
                    <p className="text-muted-foreground">
                      {refusalRecovery(attempt.failure.refusalReason)}
                    </p>
                  ) : null}
                  {attempt.failure.kind === "candidate_validator_mismatch" ? (
                    <Button asChild variant="outline">
                      <Link to={`/projects/${encodeURIComponent(projectId)}/preview`}>
                        {FAILURE_RECOVERY_LABEL[attempt.failure.kind]}
                      </Link>
                    </Button>
                  ) : FAILURE_RECOVERY_LABEL[attempt.failure.kind] === null ||
                  attempt.failure.kind === "unknown_dispatch" ||
                  attempt.failure.kind === "error" ? null : (
                    <Button variant="outline" onClick={() => recover(attempt.failure)}>
                      {FAILURE_RECOVERY_LABEL[attempt.failure.kind]}
                    </Button>
                  )}
                </div>
              ) : null}

              {sending ? (
                <p
                  role="status"
                  aria-live="polite"
                  className="rounded-md border border-border px-3 py-2 text-xs text-muted-foreground"
                >
                  Asking the preview target to start the replay…
                </p>
              ) : ready ? null : (
                <p id={startReasonId} className="text-xs text-muted-foreground">
                  {startReason}
                </p>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The command's answer, and — for every answer that created a run the target
 * may be replaying into — its progress, read from the new run's own turn
 * events. A refusal renders its closed reason and the one recovery it has.
 */
function ReplayOutcome({
  projectId,
  result,
  planned,
  pollIntervalMs,
  onRecover,
}: {
  projectId: string;
  result: ReplayResultView;
  planned: number | null;
  pollIntervalMs: number;
  onRecover: (recovery: ReplayRefusalRecovery) => void;
}) {
  const label = REPLAY_RESULT_LABELS[result.kind];
  if (result.kind === "refused") {
    const refusal = describeReplayRefusal(result.reasonCode);
    const recoveryLabel = REFUSAL_RECOVERY_LABEL[refusal.recovery];
    return (
      <div role="alert" className="space-y-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-foreground">
        <p className="flex flex-wrap items-center gap-2">
          <Badge tone="destructive">{label}</Badge>
          <span className="font-medium">{refusal.title}</span>
        </p>
        <p>{refusal.description}</p>
        {result.reasonCode === null ? null : (
          <p className="text-xs text-muted-foreground">
            Reason code: <code dir="ltr">{result.reasonCode}</code>
          </p>
        )}
        {refusal.recovery === "operator" ? (
          <p className="text-xs text-muted-foreground">
            An operator of the preview target acts on this; there is nothing to retry from here.
          </p>
        ) : null}
        {recoveryLabel === null ? null : (
          <Button variant="outline" onClick={() => onRecover(refusal.recovery)}>
            {recoveryLabel}
          </Button>
        )}
        {refusal.recovery === "open_run" ? (
          <Button variant="outline" asChild>
            <Link to={previewRunPath(projectId, result.previewRunRef)}>Open replay run</Link>
          </Button>
        ) : null}
      </div>
    );
  }
  if (result.kind === "unreadable") {
    return (
      <div role="alert" className="space-y-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-foreground">
        <p>
          The Editor Service reports the command was dispatched but carries no
          status for it, which this build cannot read as any state. The new run
          exists; open it to see what the preview target did.
        </p>
        <Button variant="outline" asChild>
          <Link to={previewRunPath(projectId, result.previewRunRef)}>Open replay run</Link>
        </Button>
      </div>
    );
  }
  return (
    <ReplayProgress
      projectId={projectId}
      result={result}
      planned={planned}
      pollIntervalMs={pollIntervalMs}
    />
  );
}

function resultSentence(
  result: Extract<
    ReplayResultView,
    { kind: "queued" | "running" | "completed" | "partial" | "already_dispatched" | "unresolved" }
  >,
): string {
  switch (result.kind) {
    case "queued":
      return "The preview target queued the replay. Its worker sends the inputs in order as it runs; this view re-reads the new run while it does.";
    case "running":
      return "The preview target is replaying the inputs now. This view re-reads the new run while it does.";
    case "completed":
      return "The preview target reports the replay completed: every planned input was delivered and answered.";
    case "partial":
      return `${describeReplayRefusal(result.reasonCode).description} Some but not all planned turns were replayed; the new run is marked incomplete and must not be read as a complete run.`;
    case "already_dispatched":
      return "This attempt was already started: the Editor Service recognised the earlier command and recorded the target's answer to it, so nothing was sent again and no second budget was spent.";
    case "unresolved":
      return "The Editor Service reserved this replay but never recorded the preview target's answer, and it did not send the command again. Whether the replay started is known only from the new run below.";
  }
}

function ReplayProgress({
  projectId,
  result,
  planned,
  pollIntervalMs,
}: {
  projectId: string;
  result: Extract<
    ReplayResultView,
    { kind: "queued" | "running" | "completed" | "partial" | "already_dispatched" | "unresolved" }
  >;
  planned: number | null;
  pollIntervalMs: number;
}) {
  const inspect = useCapability("preview.inspect");
  // The target reported an end for these two; the others may still move.
  const terminal = result.kind === "completed" || result.kind === "partial";
  const progressOf = (reading: TurnEventsReading) =>
    reading.events === null
      ? null
      : readReplayProgress(readTurnBoundaries(reading.events), planned);
  const turns = usePreviewRunTurnsQuery(projectId, result.previewRunRef, {
    enabled: inspect.allowed,
    // Re-read while the replay can still change: never after the target
    // reported an end, and never once every planned turn is recorded. A
    // failed read stops the timer; the refresh control is the retry.
    pollIntervalMs: terminal
      ? false
      : (reading) => (progressOf(reading)?.complete ? false : pollIntervalMs),
  });
  const reading = turns.data === undefined ? null : progressOf(turns.data);
  const polling =
    inspect.allowed && !terminal && !(reading?.complete ?? false) && !turns.isError;

  const ready = turns.data !== undefined;
  const runPath = previewRunPath(projectId, result.previewRunRef);
  const tone =
    result.kind === "completed"
      ? "success"
      : result.kind === "partial"
        ? "warning"
        : result.kind === "unresolved"
          ? "warning"
          : "accent";

  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
      <div role="status" aria-live="polite" aria-atomic="true" className="space-y-1">
        <p className="flex flex-wrap items-center gap-2">
          <Badge tone={tone}>{REPLAY_RESULT_LABELS[result.kind]}</Badge>
          <span className="text-sm font-medium">Replay {result.kind === "partial" ? "partly done" : "started"}</span>
        </p>
        <p className="text-sm text-foreground">{resultSentence(result)}</p>
      </div>

      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Progress</dt>
          <dd className="mt-0.5 text-foreground">
            {!inspect.allowed
              ? "Progress is read from the new run, which needs the preview.inspect permission this account does not hold."
              : turns.isPending
                ? "Reading the new run…"
                : turns.isError
                  ? "The new run could not be read yet."
                  : reading === null
                    ? "The new run has no readable trace yet, so no turn has been recorded."
                    : describeReplayProgress(reading)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Capture</dt>
          <dd className="mt-0.5 text-foreground">
            {turns.data ? CAPTURE_STATE_LABELS[turns.data.run.captureState] : "Not read yet"}
          </dd>
        </div>
      </dl>

      {turns.isError ? (
        <p role="alert" className="text-xs text-foreground">
          {describeTurnReadFailure(turns.error)}
        </p>
      ) : null}

      <p className="text-xs text-muted-foreground">
        {polling
          ? "Re-reads the new run while the replay can still change. Refreshing is a read and never resends the command."
          : "Automatic re-reads stopped: the target reported an end, the planned turns are all recorded, or the last read failed. Refreshing is a read and never resends the command."}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => void turns.refetch()}
          disabled={!inspect.allowed || turns.isFetching}
        >
          <RefreshCw aria-hidden="true" className="h-4 w-4" />
          Refresh progress
        </Button>
        {ready ? (
          <Button asChild>
            <Link to={runPath}>Open replay run</Link>
          </Button>
        ) : (
          <Button disabled aria-describedby={`replay-open-reason-${result.previewRunRef}`}>
            Open replay run
          </Button>
        )}
      </div>
      {ready ? null : (
        <p id={`replay-open-reason-${result.previewRunRef}`} className="text-xs text-muted-foreground">
          {inspect.allowed
            ? "The new run is not readable yet. It opens once the preview target has bound it; refresh to check."
            : "Opening the new run needs the preview.inspect permission this account does not hold."}
        </p>
      )}
    </div>
  );
}

function describeTurnReadFailure(error: unknown): string {
  const failure = classifyRunReadFailure(error);
  switch (failure.kind) {
    case "not_found":
      return "No run is readable at the new locator yet for your account in this environment. The preview target may not have bound it yet; refresh to check.";
    case "denied":
      return "Your account is not authorized to inspect the new run.";
    case "upstream":
      return failure.recovery === "retry"
        ? "The preview target took too long to answer; the same read may well succeed."
        : "The preview target could not answer the read; an identical read is refused until an operator acts.";
    case "transport":
      return "No response arrived for the read; check the connection and refresh.";
    case "unauthenticated":
      return "Your sign-in has expired.";
    case "not_implemented":
      return "Reading runs is not built in this environment.";
    case "error":
      return `The Editor Service answered the read with "${failure.code}".`;
  }
}
